import { useMemo } from 'react';
import Card, { CardHeader, CardBody } from '../ui/Card';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface IncomeStatementData {
  revenue?: string | null;
  costOfRevenue?: string | null;
  grossProfit?: string | null;
  researchDevelopment?: string | null;
  salesMarketing?: string | null;
  generalAdministrative?: string | null;
  totalOperatingExpenses?: string | null;
  depreciationAmortization?: string | null;
  operatingIncome?: string | null;
  netIncome?: string | null;
}

interface Report {
  reportId: string;
  period: string;
  type: string;
  incomeStatement?: IncomeStatementData;
}

interface ComparativeIncomeStatementTableProps {
  reports: Report[];
  mode: 'single' | 'annual' | 'quarterly';
  maxPeriods: number; // 3, 5, or 10
}

interface ChangeIndicator {
  percentage: number;
  direction: 'up' | 'down' | 'neutral';
  color: string;
}

// Parse currency string to number
const parseCurrency = (value: string | null | undefined): number | null => {
  if (!value) return null;
  // Remove $, commas, B/M suffixes
  const cleaned = value.replace(/[$,\s]/g, '');

  let multiplier = 1;
  if (cleaned.includes('B')) {
    multiplier = 1_000_000_000;
  } else if (cleaned.includes('M')) {
    multiplier = 1_000_000;
  } else if (cleaned.includes('K')) {
    multiplier = 1_000;
  }

  const numStr = cleaned.replace(/[BMK]/g, '');
  const num = parseFloat(numStr);

  return isNaN(num) ? null : num * multiplier;
};

// Calculate percentage change
const calculateChange = (current: string | null | undefined, previous: string | null | undefined): ChangeIndicator | null => {
  const currentVal = parseCurrency(current);
  const previousVal = parseCurrency(previous);

  if (currentVal === null || previousVal === null || previousVal === 0) {
    return null;
  }

  const change = ((currentVal - previousVal) / Math.abs(previousVal)) * 100;

  return {
    percentage: change,
    direction: Math.abs(change) < 0.5 ? 'neutral' : change > 0 ? 'up' : 'down',
    color: Math.abs(change) < 0.5 ? 'text-neutral-600' : change > 0 ? 'text-success-600' : 'text-error-600'
  };
};

// Format value for display
const formatValue = (value: string | null | undefined): string => {
  if (!value) return 'N/A';
  return value;
};

export default function ComparativeIncomeStatementTable({
  reports,
  mode,
  maxPeriods
}: ComparativeIncomeStatementTableProps) {
  // Filter and limit reports based on mode and maxPeriods
  const displayedReports = useMemo(() => {
    let filtered: Report[];

    if (mode === 'annual') {
      filtered = reports.filter(r => r.type === 'Annual' || r.period.includes('FY'));
    } else if (mode === 'quarterly') {
      filtered = reports.filter(r => r.type === 'Quarterly' || r.period.match(/Q[1-4]/));
    } else {
      // Single mode - just latest
      filtered = reports.slice(0, 1);
    }

    // Limit to maxPeriods
    return filtered.slice(0, maxPeriods);
  }, [reports, mode, maxPeriods]);

  // Define rows with hierarchy
  const rows = useMemo(() => {
    return [
      // Revenue Section
      { type: 'header', label: 'REVENUE', field: null },
      { type: 'total', label: 'Total Revenue', field: 'revenue' },
      { type: 'item', label: 'Cost of Revenue', field: 'costOfRevenue' },
      { type: 'total', label: 'Gross Profit', field: 'grossProfit' },

      // Operating Expenses Section
      { type: 'header', label: 'OPERATING EXPENSES', field: null },
      { type: 'item', label: 'Research & Development', field: 'researchDevelopment' },
      { type: 'item', label: 'Sales & Marketing', field: 'salesMarketing' },
      { type: 'item', label: 'General & Administrative', field: 'generalAdministrative' },
      { type: 'item', label: 'Depreciation & Amortization', field: 'depreciationAmortization' },
      { type: 'category', label: 'Total Operating Expenses', field: 'totalOperatingExpenses' },

      // Income Section
      { type: 'header', label: 'INCOME', field: null },
      { type: 'total', label: 'Operating Income', field: 'operatingIncome' },
      { type: 'total', label: 'Net Income', field: 'netIncome' },
    ];
  }, []);

  // Render cell with change indicator
  const renderCell = (report: Report, field: string | null, columnIndex: number) => {
    if (!field) return null;

    const value = report.incomeStatement?.[field as keyof IncomeStatementData];
    const previousReport = displayedReports[columnIndex + 1];

    // Calculate change vs previous period
    let changeIndicator: ChangeIndicator | null = null;
    if (mode !== 'single' && previousReport) {
      const previousValue = previousReport.incomeStatement?.[field as keyof IncomeStatementData];
      changeIndicator = calculateChange(value, previousValue);
    }

    return (
      <div className="flex items-center justify-end gap-2">
        <span>{formatValue(value)}</span>
        {changeIndicator && (
          <span className={`flex items-center text-xs font-medium ${changeIndicator.color}`}>
            {changeIndicator.direction === 'up' && <TrendingUp className="h-3 w-3 mr-0.5" />}
            {changeIndicator.direction === 'down' && <TrendingDown className="h-3 w-3 mr-0.5" />}
            {changeIndicator.direction === 'neutral' && <Minus className="h-3 w-3 mr-0.5" />}
            {Math.abs(changeIndicator.percentage).toFixed(1)}%
          </span>
        )}
      </div>
    );
  };

  // Get row styling based on type
  const getRowClass = (type: string) => {
    switch (type) {
      case 'header':
        return 'bg-neutral-100 font-bold text-neutral-900';
      case 'category':
        return 'bg-primary-50 font-semibold text-primary-900';
      case 'total':
        return 'bg-neutral-50 font-bold text-neutral-900 border-t-2 border-neutral-300';
      case 'item':
        return 'text-neutral-700';
      default:
        return '';
    }
  };

  const getLabelClass = (type: string) => {
    switch (type) {
      case 'header':
        return 'py-3 px-4';
      case 'category':
        return 'py-2 px-4 pl-6';
      case 'total':
        return 'py-3 px-4 pl-6';
      case 'item':
        return 'py-2 px-4 pl-10';
      default:
        return 'py-2 px-4';
    }
  };

  if (displayedReports.length === 0) {
    return (
      <Card>
        <CardBody className="text-center py-8 text-neutral-600">
          No income statement data available
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">
              Income Statement {mode === 'single' ? 'Detail' : 'Comparison'}
            </h3>
            <p className="text-sm text-neutral-600 mt-1">
              {mode === 'annual' && `Showing ${displayedReports.length} Annual Report${displayedReports.length > 1 ? 's' : ''}`}
              {mode === 'quarterly' && `Showing ${displayedReports.length} Quarter${displayedReports.length > 1 ? 's' : ''}`}
              {mode === 'single' && displayedReports[0]?.period}
            </p>
          </div>
          {mode !== 'single' && (
            <div className="text-xs text-neutral-500">
              <span className="text-success-600 font-medium">↑ Increase</span>
              {' · '}
              <span className="text-error-600 font-medium">↓ Decrease</span>
            </div>
          )}
        </div>
      </CardHeader>
      <CardBody>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white border-b-2 border-neutral-300">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-neutral-700 min-w-[250px]">
                  Category
                </th>
                {displayedReports.map((report, idx) => (
                  <th key={report.reportId} className="text-right py-3 px-4 font-semibold text-neutral-700 min-w-[150px]">
                    <div className="flex flex-col items-end">
                      <span>{report.period}</span>
                      {idx === 0 && mode !== 'single' && (
                        <span className="text-xs font-normal text-primary-600">Latest</span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => {
                const rowClass = getRowClass(row.type);
                const labelClass = getLabelClass(row.type);

                // For header rows without field, span all columns
                if (!row.field) {
                  return (
                    <tr key={rowIndex} className={rowClass}>
                      <td colSpan={displayedReports.length + 1} className={labelClass}>
                        {row.label}
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr key={rowIndex} className={`${rowClass} hover:bg-neutral-50`}>
                    <td className={labelClass}>{row.label}</td>
                    {displayedReports.map((report, colIndex) => (
                      <td key={report.reportId} className="py-2 px-4 text-right">
                        {renderCell(report, row.field, colIndex)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}
